import { CovChart } from '@components/chartTemplates/CovChart';
import { EnergyChart } from '@components/chartTemplates/EnergyChart';
import { HumidityChart } from '@components/chartTemplates/HumidityChart';
import { OtherMeasureChart } from '@components/chartTemplates/OtherMeasureChart';
import { PresenceChart } from '@components/chartTemplates/PresenceChart';
import Loading from '@components/loading/Loading';
import { batiment } from '@tsTypes/mySitesTypes';
import { sensor, sensorType } from '@tsTypes/sensorType';
import { formatDistanceToNow, subHours } from 'date-fns';
import React, { useMemo, useRef } from 'react';
import HighchartsBoost from 'highcharts/modules/boost';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { formatChartDatas, formatDeltaTempChartDatas } from '@utils/chartFunctions';
import { useDataFetch } from '@hooks/useDataFetch';
import useSWR from 'swr';
import { GetFetcher } from '@api/SwrFetcher';
import { fr } from 'date-fns/locale';
import { IntensityChart } from '@components/chartTemplates/IntensityChart';
import { DeltaTempChart } from '@components/chartTemplates/DeltaTempChart';

interface props {
	currentSensor: sensor;
	currentBatiment: batiment;
}

//modèle de graphique en fonction du type de capteur
const chartModel: Record<sensorType, any> = {
    électricité: EnergyChart,
    gaz: EnergyChart,
    température: OtherMeasureChart,
    humidité: HumidityChart,
    CO2: OtherMeasureChart,
    COV: CovChart,
    "différence de pression": OtherMeasureChart,
    luminosité: OtherMeasureChart,
    pression: OtherMeasureChart,
    présence: PresenceChart,
    intensité: IntensityChart,
    "delta température": DeltaTempChart,
    "couple-moteur": OtherMeasureChart,
    "position-moteur": OtherMeasureChart,
    "monoxyde de carbone": OtherMeasureChart,
    ozone: OtherMeasureChart,
    "dioxyde d'azote": OtherMeasureChart,
    formaldéhyde: OtherMeasureChart,
    "matière particule 01": OtherMeasureChart,
    "matière particule 02,5": OtherMeasureChart,
    "matière particule 04": OtherMeasureChart,
    "matière particule 10": OtherMeasureChart,
    "indice qualité de l'air": OtherMeasureChart,
    "protection contre les virus": OtherMeasureChart,
    "confort thermohygrométrique": OtherMeasureChart,
    "efficacité ventilation": OtherMeasureChart,
};

export default function ChartDashboardBatiment({ currentSensor, currentBatiment }: props) {
	HighchartsBoost(Highcharts);

	const chartRef = useRef<HighchartsReactRefObject>(null);

	const { data: maxDate } = useSWR(
		{
			url: '/sensor/get-min-max-mesures',
			params: {
				idSensor: currentSensor.id_compteur,
				type: currentSensor.type_energie,
			},
		},
		GetFetcher,
		{ revalidateOnFocus: false }
	);

	//on récupère les données des mesures
	const { data, isLoading } = useDataFetch('/sensor/get-mesures', {
		idSensor: currentSensor.id_compteur,
		type: currentSensor.type_energie,
		endDate: maxDate?.max?.timestamp,
		startDate: subHours(new Date(maxDate?.max?.timestamp * 1000), 24)
			.getTime()
			.toString()
			.slice(0, -3),
	});

	//on formate les données pour les afficher dans le graphique
	//eslint-disable-next-line
	const mesure = useMemo(() => {
		if (currentSensor.type_energie !== 'delta température') {
			return formatChartDatas(data?.mesures, currentSensor);
		} else {
			return formatDeltaTempChartDatas(data?.mesures, currentSensor);
		}
	}, [data, currentSensor]);

	if (isLoading || !currentBatiment) return <Loading />;

	return (
		<div id="chart-dashboard-batiment">
			{mesure.mesure.length > 0 && maxDate?.max?.timestamp ? (
				<>
					<HighchartsReact
						highcharts={Highcharts}
						options={chartModel[currentSensor.type_energie](
							currentSensor,
							currentBatiment,
							mesure.mesure,
							mesure.index,
							[],
							null,
							true
						)}
						ref={chartRef}
					/>
					<p id="time-ref">
						Dernière mesure{' '}
						{formatDistanceToNow(new Date(maxDate?.max.timestamp * 1000), {
							addSuffix: true,
							locale: fr,
						})}
					</p>
				</>
			) : (
				<h1>Pas de données ces dernières 24h</h1>
			)}
		</div>
	);
}
